/**
 * Chat Threads Service for Client-Side Operations
 * Handles all chat thread CRUD operations using Supabase directly
 * Matches the exact structure from server/services/supabase-chat-threads.service.ts
 */

import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";
import type { ChatThread, InsertChatThread } from "@shared/schema";
import { autoTitleFromPlainText } from "@/lib/autoTitleFromText";
import { dedupeIds } from "@/lib/threadRelationshipsPure";
import { getThoughtProcessPreferences } from "@/lib/thoughtProcessPreferences";

function scheduleThreadBrief(userId: string, threadId: string, title: string | null | undefined, messages: unknown): void {
    if (!userId || !threadId || !Array.isArray(messages)) return;
    const content = messages
        .map((message) => {
            if (!message || typeof message !== "object") return "";
            const row = message as { role?: string; content?: unknown };
            const text = typeof row.content === "string" ? row.content : "";
            return `${row.role ?? "user"}: ${text}`;
        })
        .join("\n");
    if (!content.trim()) return;
    void import("@/services/operatingLoopService").then(({ scheduleOperatingBrief }) =>
        scheduleOperatingBrief({
            userId,
            documentId: `thread:${threadId}`,
            title: title || "Thread",
            content,
        }),
    );
}

function pickMergedFromSpaceId(row: any): string | undefined {
    const v = row?.mergedFromSpaceId ?? row?.merged_from_space_id;
    if (v == null || v === "") return undefined;
    return String(v);
}

export type AttachedGitHubRepoRef = { owner: string; name: string; fullName: string };

const MAX_ATTACHED_GITHUB_REPOS = 5;

function normalizeAttachedGitHubRepos(raw: unknown): AttachedGitHubRepoRef[] {
    const arr = Array.isArray(raw) ? raw : [];
    const out: AttachedGitHubRepoRef[] = [];
    for (const item of arr) {
        if (!item || typeof item !== "object") continue;
        const owner = String((item as { owner?: string }).owner ?? "").trim();
        const name = String((item as { name?: string }).name ?? "").trim();
        const fullName =
            String((item as { fullName?: string; full_name?: string }).fullName ??
                (item as { full_name?: string }).full_name ??
                "").trim() || (owner && name ? `${owner}/${name}` : "");
        if (!owner || !name || !fullName) continue;
        if (out.some((r) => r.fullName === fullName)) continue;
        out.push({ owner, name, fullName });
        if (out.length >= MAX_ATTACHED_GITHUB_REPOS) break;
    }
    return out;
}

const THREAD_SUMMARY_SELECT_WITH_ARCHIVE =
    "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt";

const THREAD_BULK_SELECT_WITH_ARCHIVE =
    "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt";

const DEBUG_THREAD_ACCESS =
    import.meta.env.DEV || import.meta.env.VITE_DEBUG_THREAD_ACCESS === "true";

function logThreadAccess(phase: string, detail: Record<string, unknown>) {
    if (DEBUG_THREAD_ACCESS) {
        console.log("[chatThreadsService.getChatThread]", phase, detail);
    }
}

export type ThreadSummary = Omit<ChatThread, "messages"> & { messageCount: number };

export type AccessibleThreadSummary = ThreadSummary & {
    accessRole?: string;
    workspaceId?: string | null;
    ownerUserId?: string;
};

export type AccessibleThreadsFetchResult = {
    threads: AccessibleThreadSummary[];
    usedOwnedFallback?: boolean;
};

/** Resolves UI scope to list_accessible_thread_summaries RPC parameter. */
export function resolveThreadWorkspaceScope(
    scope: "all" | "personal" | "trash" | "current",
    currentWorkspaceId?: string | null
): string {
    if (scope === "all") return "all";
    if (scope === "personal") return "personal";
    if (scope === "trash") return "trash";
    if (currentWorkspaceId) return `workspace:${currentWorkspaceId}`;
    return "personal";
}

class ChatThreadsService {
    /**
     * Generate a simple unique ID - matches server implementation
     */
    private generateSimpleUniqueId(): string {
        const timestamp = Date.now().toString(36);
        const randomPart = Math.random().toString(36).substring(2, 9);
        return timestamp + randomPart;
    }

    /**
     * Get current user ID from centralized session (single auth call on load)
     */
    private async getCurrentUserId(): Promise<string> {
        if (!isSupabaseConfigured) {
            throw new Error("Service not configured. Please try again later.");
        }
        const userId = await getCurrentUserId();
        if (!userId) {
            throw new Error("User not authenticated. Please log in.");
        }
        return userId;
    }

    /** Auto-embed thread after message saves (debounced via ragService). */
    private maybeScheduleThreadEmbed(
        threadId: string,
        updates: Partial<InsertChatThread>,
        thread: ChatThread
    ): void {
        if (updates.messages === undefined) return;
        if (!getThoughtProcessPreferences().autoEmbedThreads) return;

        const includeInRag =
            (updates as { includeInRag?: boolean }).includeInRag ?? thread.includeInRag;
        if (includeInRag === false) return;

        const messages = updates.messages;
        if (!Array.isArray(messages) || messages.length === 0) return;

        void import("@/services/ragService").then(({ ragService }) => {
            ragService.scheduleEmbedThread(threadId);
        });
    }

    /**
     * Get all chat threads for the current user (metadata only — messages are empty).
     * @deprecated For list views use {@link getThreadSummaries}. For full messages use {@link getChatThread} or {@link getChatThreadsForExport}.
     * @param workspaceId - When null/undefined: personal threads (workspace_id IS NULL). When set: threads for that workspace.
     */
    async getChatThreads(workspaceId?: string | null): Promise<ChatThread[]> {
        if (import.meta.env.DEV) {
            console.warn(
                "getChatThreads: bulk fetch without messages. Use getThreadSummaries for lists or getChatThread(id) for one thread."
            );
        }
        const userId = await this.getCurrentUserId();

        let query = supabase
            .from("chat-threads")
            .select(THREAD_BULK_SELECT_WITH_ARCHIVE)
            .eq("userId", userId);
        if (workspaceId != null && workspaceId !== "") {
            query = query.eq("workspace_id", workspaceId);
        } else {
            query = query.is("workspace_id", null);
        }
        const { data, error } = await query.order("updatedAt", { ascending: false });

        if (error) {
            console.error("Error fetching chat threads:", error);
            throw new Error(`Failed to fetch chat threads: ${error.message}`);
        }

        return (data || []).map((thread: any) => this.mapToThread({ ...thread, messages: [] }));
    }

    /**
     * Load full threads (with messages) for backup/export. Fetches summaries first, then each thread individually.
     */
    async getChatThreadsForExport(workspaceId?: string | null): Promise<ChatThread[]> {
        const summaries = await this.getThreadSummaries(workspaceId);
        const CONCURRENCY = 4;
        const results: ChatThread[] = [];

        for (let i = 0; i < summaries.length; i += CONCURRENCY) {
            const batch = summaries.slice(i, i + CONCURRENCY);
            const fetched = await Promise.all(
                batch.map(async (summary) => {
                    try {
                        const thread = await this.getChatThread(summary.id, workspaceId);
                        return thread;
                    } catch (e) {
                        console.error(`Failed to fetch thread ${summary.id} for export:`, e);
                        return null;
                    }
                })
            );
            for (const thread of fetched) {
                if (thread) results.push(thread);
            }
        }

        return results;
    }

    private isArchivedFromRow(thread: any): boolean {
        if (thread?.status === "archived") return true;
        // Schema uses camelCase: isArchived (character varying(5))
        return (
            thread?.isArchived === "true" ||
            thread?.isArchived === true
        );
    }

    private isLockedFromRow(thread: any): boolean {
        return (
            thread?.isLocked === true ||
            thread?.isLocked === "true" ||
            thread?.is_locked === true ||
            thread?.is_locked === "true" ||
            !!(thread?.lockedAt ?? thread?.locked_at)
        );
    }

    private mapToThread(thread: any): ChatThread {
        const isArchived = this.isArchivedFromRow(thread);
        const isLocked = this.isLockedFromRow(thread);
        
        // Support both topicIds array (new) and topicId single value (backward compatibility).
        // topicId is exposed as the primary topic (first of topicIds) so single-topic consumers
        // (link dialog, badges) still see the linked topic instead of it appearing "forgotten".
        const topicIds = Array.isArray(thread.topicIds)
            ? thread.topicIds.filter((id: any) => id != null).map((id: any) => String(id))
            : [];
        const topicId = topicIds.length > 0
            ? topicIds[0]
            : (thread.topicId || null);

        // Support both linkedSpaceIds array (already jsonb) - ensure it's always an array
        const linkedSpaceIds = Array.isArray(thread.linkedSpaceIds)
            ? thread.linkedSpaceIds.filter((id: any) => id != null).map((id: any) => String(id))
            : (thread.linkedSpaceIds ? [String(thread.linkedSpaceIds)] : []);
        const attachedGitHubRepos = normalizeAttachedGitHubRepos(
            thread.attachedGitHubRepos ?? thread.attached_github_repos
        );

        return {
            id: thread.id,
            userId: thread.userId,
            title: thread.title,
            topic: thread.topic,
            topicId: topicId,
            topicIds: topicIds.length > 0 ? topicIds : undefined, // Only include if array exists
            tags: thread.tags,
            messages: thread.messages ?? [],
            linkedSpaceIds: linkedSpaceIds,
            attachedGitHubRepos,
            mergedFromSpaceId: pickMergedFromSpaceId(thread),
            agentId: thread.agentId,
            contextMode: thread.contextMode,
            status: thread.status,
            isArchived: isArchived ? "true" : "false",
            archivedAt: thread.archivedAt ?? null, // Schema: archivedAt (camelCase)
            archiveSummaryDocId: thread.archiveSummaryDocId ?? null, // Schema: archiveSummaryDocId (camelCase)
            isLocked: isLocked ? "true" : "false",
            lockedAt: thread.locked_at ?? null, // Schema: locked_at (snake_case)
            includeInRag: thread.includeInRag !== false,
            createdAt: thread.createdAt ?? thread.created_at,
            updatedAt: thread.updatedAt ?? thread.updated_at,
        } as ChatThread;
    }

    private mapRpcSummaryRow(thread: any): AccessibleThreadSummary {
        const base = this.mapToThreadSummary(thread, thread.messageCount ?? 0);
        return {
            ...base,
            accessRole: thread.access_role ?? thread.accessRole,
            workspaceId: thread.workspace_id ?? thread.workspaceId ?? null,
            ownerUserId: thread.owner_user_id ?? thread.ownerUserId,
        };
    }

    /**
     * Thread summary type - lightweight version without messages
     */
    private mapToThreadSummary(thread: any, messageCount?: number): Omit<ChatThread, 'messages'> & { messageCount: number } {
        const count = messageCount ?? (Array.isArray(thread.messages) ? thread.messages.length : 0);
        const isArchived = this.isArchivedFromRow(thread);
        const isLocked = this.isLockedFromRow(thread);
        
        // Support both topicIds array (new) and topicId single value (backward compatibility).
        // topicId is exposed as the primary topic (first of topicIds) so single-topic consumers
        // (link dialog, badges) still see the linked topic instead of it appearing "forgotten".
        const topicIds = Array.isArray(thread.topicIds)
            ? thread.topicIds.filter((id: any) => id != null).map((id: any) => String(id))
            : [];
        const topicId = topicIds.length > 0
            ? topicIds[0]
            : (thread.topicId || null);

        // Support both linkedSpaceIds array (already jsonb) - ensure it's always an array
        const linkedSpaceIds = Array.isArray(thread.linkedSpaceIds)
            ? thread.linkedSpaceIds.filter((id: any) => id != null).map((id: any) => String(id))
            : (thread.linkedSpaceIds ? [String(thread.linkedSpaceIds)] : []);
        const attachedGitHubRepos = normalizeAttachedGitHubRepos(
            thread.attachedGitHubRepos ?? thread.attached_github_repos
        );

        return {
            id: thread.id,
            userId: thread.userId,
            title: thread.title,
            topic: thread.topic,
            topicId: topicId,
            topicIds: topicIds.length > 0 ? topicIds : undefined, // Only include if array exists
            tags: thread.tags,
            linkedSpaceIds: linkedSpaceIds,
            attachedGitHubRepos,
            mergedFromSpaceId: pickMergedFromSpaceId(thread),
            agentId: thread.agentId,
            contextMode: thread.contextMode,
            status: thread.status,
            isArchived: isArchived ? "true" : "false",
            archivedAt: thread.archivedAt ?? null, // Schema: archivedAt (camelCase)
            archiveSummaryDocId: thread.archiveSummaryDocId ?? null, // Schema: archiveSummaryDocId (camelCase)
            isLocked: isLocked ? "true" : "false",
            lockedAt: thread.locked_at ?? null, // Schema: locked_at (snake_case)
            includeInRag: thread.includeInRag !== false,
            createdAt: thread.createdAt ?? thread.created_at,
            updatedAt: thread.updatedAt ?? thread.updated_at,
            messageCount: count,
        };
    }

    private isRpcTimeoutError(error: { code?: string; message?: string } | null): boolean {
        if (!error) return false;
        return (
            error.code === "57014" ||
            (error.message?.toLowerCase().includes("timeout") ?? false)
        );
    }

    private parseWorkspaceScopeFilter(workspaceScope: string): {
        showTrash: boolean;
        wsFilter: string;
        wsUuid: string | null;
    } {
        const wsFilter = (workspaceScope || "all").toLowerCase();
        const showTrash = wsFilter === "trash";
        let wsUuid: string | null = null;
        if (wsFilter.startsWith("workspace:")) {
            wsUuid = wsFilter.slice("workspace:".length) || null;
        }
        return { showTrash, wsFilter, wsUuid };
    }

    /**
     * Fast path: owned threads only (no shared-space RPC). Used when list_accessible_thread_summaries times out.
     */
    async getAllOwnedThreadSummaries(
        workspaceScope: string = "all"
    ): Promise<AccessibleThreadSummary[]> {
        const userId = await this.getCurrentUserId();
        const { showTrash, wsFilter, wsUuid } = this.parseWorkspaceScopeFilter(workspaceScope);
        const select =
            "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt, workspace_id, deleted_at";

        let q = supabase
            .from("chat-threads")
            .select(select)
            .eq("userId", userId)
            .order("updatedAt", { ascending: false })
            .limit(2000);

        if (showTrash) {
            q = q.not("deleted_at", "is", null);
        } else {
            q = q.is("deleted_at", null);
        }

        if (wsFilter === "personal") {
            q = q.is("workspace_id", null);
        } else if (wsFilter.startsWith("workspace:") && wsUuid) {
            q = q.eq("workspace_id", wsUuid);
        }

        const { data, error } = await q;
        if (error) {
            throw new Error(`Failed to fetch owned thread summaries: ${error.message}`);
        }

        return (data || []).map((row: any) => ({
            ...this.mapToThreadSummary(row, 0),
            accessRole: "owner",
            workspaceId: row.workspace_id ?? null,
            ownerUserId: userId,
        }));
    }

    private mapRpcRowsToSummaries(data: unknown): AccessibleThreadSummary[] {
        const rows = Array.isArray(data) ? data : data ? JSON.parse(JSON.stringify(data)) : [];
        if (!Array.isArray(rows)) return [];
        return rows.map((row: any) => this.mapRpcSummaryRow(row));
    }

    /**
     * All threads the user can access (owner, invite-shared, space editor/viewer) across workspaces.
     */
    async getAccessibleThreadSummaries(
        workspaceScope: string = "all"
    ): Promise<AccessibleThreadsFetchResult> {
        await this.getCurrentUserId();
        const { data, error } = await supabase.rpc("list_accessible_thread_summaries", {
            p_workspace_scope: workspaceScope,
        });

        if (error) {
            console.error("list_accessible_thread_summaries:", error);
            if (this.isRpcTimeoutError(error)) {
                console.warn(
                    "[chatThreadsService] RPC timed out; falling back to owned threads only"
                );
                const threads = await this.getAllOwnedThreadSummaries(workspaceScope);
                return { threads, usedOwnedFallback: true };
            }
            throw new Error(`Failed to fetch accessible threads: ${error.message}`);
        }

        return { threads: this.mapRpcRowsToSummaries(data) };
    }

    /**
     * Get lightweight thread summaries for a specific space (server-side filtered)
     * Much faster than getChatThreads - excludes full message content
     */
    async getThreadSummariesBySpace(spaceId: string): Promise<Array<Omit<ChatThread, 'messages'> & { messageCount: number }>> {
        const userId = await this.getCurrentUserId();
        
        // Create timeout to prevent hanging
        const TIMEOUT_MS = 20000; // 20 seconds
        
        // First, fetch threads for this user, then filter client-side for linkedSpaceIds
        // (Supabase contains filter on JSONB arrays can be problematic)
        const selectWithArchiveFields = "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt";
        const selectBaseFields = "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, is_locked, locked_at, includeInRag, createdAt, updatedAt";

        const runQuery = async (select: string) => {
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Query timeout')), TIMEOUT_MS)
            );
            const queryPromise = supabase
                .from("chat-threads")
                .select(select)
                .eq("userId", userId)
                .order("updatedAt", { ascending: false })
                .limit(1000);
            return await Promise.race([queryPromise, timeoutPromise]) as any;
        };

        try {
            let { data, error } = await runQuery(selectWithArchiveFields);

            if (error) {
                if (error.message?.includes('timeout')) {
                    console.error(`Query timeout for getThreadSummariesBySpace (user: ${userId}, space: ${spaceId})`);
                    return []; // Return empty array on timeout
                }
                if (error.message?.includes("column") && error.message?.includes("does not exist")) {
                    const retry = await runQuery(selectBaseFields);
                    if (retry.error) {
                        console.error("Error fetching thread summaries by space:", retry.error);
                        throw new Error(`Failed to fetch thread summaries: ${retry.error.message}`);
                    }
                    data = retry.data;
                } else {
                    console.error("Error fetching thread summaries by space:", error);
                    throw new Error(`Failed to fetch thread summaries: ${error.message}`);
                }
            }

            // Filter threads that are linked to this space
            const filteredData = (data || []).filter((thread: any) => {
                const linkedIds = thread.linkedSpaceIds ?? thread.linked_space_ids ?? [];
                return Array.isArray(linkedIds) && linkedIds.includes(spaceId);
            });

            return filteredData.map((thread: any) => this.mapToThreadSummary(thread, 0));
        } catch (error: any) {
            if (error.message?.includes('timeout')) {
                console.error(`Query timeout for getThreadSummariesBySpace (user: ${userId}, space: ${spaceId})`);
                return [];
            }
            throw error;
        }
    }

    /**
     * Get thread summaries for multiple users (for shared spaces)
     * Fetches threads owned by any of the provided user IDs
     */
    async getThreadSummariesForUsers(userIds: string[]): Promise<Array<Omit<ChatThread, 'messages'> & { messageCount: number }>> {
        if (userIds.length === 0) return [];
        
        const TIMEOUT_MS = 20000;
        const selectWithArchiveFields = "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt";
        const selectBaseFields = "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, is_locked, locked_at, includeInRag, createdAt, updatedAt";
        
        const runQuery = async (select: string) => {
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Query timeout')), TIMEOUT_MS)
            );
            const queryPromise = supabase
                .from("chat-threads")
                .select(select)
                .in("userId", userIds)
                .is("deleted_at", null)
                .order("updatedAt", { ascending: false })
                .limit(1000);
            return await Promise.race([queryPromise, timeoutPromise]) as any;
        };
        
        try {
            let { data, error } = await runQuery(selectWithArchiveFields);
            if (error) {
                if (error.message?.includes("column") && error.message?.includes("does not exist")) {
                    const retry = await runQuery(selectBaseFields);
                    if (retry.error) {
                        console.error("Error fetching thread summaries for users:", retry.error);
                        throw new Error(`Failed to fetch thread summaries: ${retry.error.message}`);
                    }
                    data = retry.data;
                } else {
                    console.error("Error fetching thread summaries for users:", error);
                    throw new Error(`Failed to fetch thread summaries: ${error.message}`);
                }
            }
            
            const summaries = (data || []).map((thread: any) => {
                const topicIds = Array.isArray(thread.topicIds)
                    ? thread.topicIds.filter((id: any) => id != null).map((id: any) => String(id))
                    : [];
                const topicId = topicIds.length > 0
                    ? topicIds[0] // Expose primary topic for single-topic consumers
                    : (thread.topicId || null);
                
                const linkedSpaceIds = Array.isArray(thread.linkedSpaceIds)
                    ? thread.linkedSpaceIds.filter((id: any) => id != null).map((id: any) => String(id))
                    : (thread.linkedSpaceIds ? [String(thread.linkedSpaceIds)] : []);
                const attachedGitHubRepos = normalizeAttachedGitHubRepos(
                    thread.attachedGitHubRepos ?? thread.attached_github_repos
                );
                
                return {
                    id: thread.id,
                    userId: thread.userId,
                    title: thread.title,
                    topic: thread.topic,
                    topicId: topicId,
                    topicIds: topicIds.length > 0 ? topicIds : undefined,
                    tags: thread.tags,
                    linkedSpaceIds: linkedSpaceIds,
                    attachedGitHubRepos,
                    mergedFromSpaceId: pickMergedFromSpaceId(thread),
                    agentId: thread.agentId,
                    contextMode: thread.contextMode,
                    status: thread.status,
                    isArchived: this.isArchivedFromRow(thread) ? "true" : "false",
                    archivedAt: thread.archivedAt ?? null,
                    archiveSummaryDocId: thread.archiveSummaryDocId ?? null,
                    isLocked: this.isLockedFromRow(thread) ? "true" : "false",
                    lockedAt: thread.locked_at ?? null,
                    createdAt: thread.createdAt ?? thread.created_at,
                    updatedAt: thread.updatedAt ?? thread.updated_at,
                    messageCount: 0,
                };
            });
            
            // Sort by updatedAt descending
            summaries.sort((a, b) => {
                const aVal = a.updatedAt ?? a.createdAt ?? 0;
                const bVal = b.updatedAt ?? b.createdAt ?? 0;
                const aTime = new Date(aVal).getTime();
                const bTime = new Date(bVal).getTime();
                return Number.isNaN(bTime) ? 1 : Number.isNaN(aTime) ? -1 : bTime - aTime;
            });
            
            return summaries;
        } catch (e: any) {
            if (e?.message?.includes('timeout')) {
                console.error(`getThreadSummariesForUsers timed out for users: ${userIds.join(', ')}`);
                return [];
            }
            throw e;
        }
    }

    /**
     * Get all thread summaries for the current user (lightweight - no messages).
     * @param workspaceId - When null/undefined: personal threads. When set: threads for that workspace. Shared threads only in personal context.
     */
    async getThreadSummaries(workspaceId?: string | null): Promise<Array<Omit<ChatThread, 'messages'> & { messageCount: number }>> {
        const userId = await this.getCurrentUserId();
        const TIMEOUT_MS = 20000;
        const selectWithArchiveFields = "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt";
        const selectBaseFields = "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, is_locked, locked_at, includeInRag, createdAt, updatedAt";
        const runQuery = async (select: string) => {
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Query timeout')), TIMEOUT_MS)
            );
            let q = supabase
                .from("chat-threads")
                .select(select)
                .eq("userId", userId)
                .is("deleted_at", null)
                .order("updatedAt", { ascending: false })
                .limit(1000);
            if (workspaceId != null && workspaceId !== "") {
                q = q.eq("workspace_id", workspaceId);
            } else {
                q = q.is("workspace_id", null);
            }
            const queryPromise = q;
            return await Promise.race([queryPromise, timeoutPromise]) as any;
        };
        try {
            let { data, error } = await runQuery(selectWithArchiveFields);
            if (error) {
                if (error.message?.includes("column") && error.message?.includes("does not exist")) {
                    const retry = await runQuery(selectBaseFields);
                    if (retry.error) {
                        console.error("Error fetching thread summaries:", retry.error);
                        throw new Error(`Failed to fetch thread summaries: ${retry.error.message}`);
                    }
                    data = retry.data;
                } else {
                    console.error("Error fetching thread summaries:", error);
                    throw new Error(`Failed to fetch thread summaries: ${error.message}`);
                }
            }
            const ownedSummaries = (data || []).map((thread: any) => {
                // Support both topicIds array (new) and topicId single value (backward compatibility)
                const topicIds = Array.isArray(thread.topicIds) 
                    ? thread.topicIds.filter((id: any) => id != null).map((id: any) => String(id))
                    : [];
                const topicId = topicIds.length > 0 
                    ? null // Clear old topicId when topicIds array exists
                    : (thread.topicId || null);
                
                // Support both linkedSpaceIds array (already jsonb) - ensure it's always an array
                const linkedSpaceIds = Array.isArray(thread.linkedSpaceIds)
                    ? thread.linkedSpaceIds.filter((id: any) => id != null).map((id: any) => String(id))
                    : (thread.linkedSpaceIds ? [String(thread.linkedSpaceIds)] : []);
                const attachedGitHubRepos = normalizeAttachedGitHubRepos(
                    thread.attachedGitHubRepos ?? thread.attached_github_repos
                );
                
                return {
                    id: thread.id,
                    userId: thread.userId,
                    title: thread.title,
                    topic: thread.topic,
                    topicId: topicId,
                    topicIds: topicIds.length > 0 ? topicIds : undefined,
                    tags: thread.tags,
                    linkedSpaceIds: linkedSpaceIds,
                    attachedGitHubRepos,
                    mergedFromSpaceId: pickMergedFromSpaceId(thread),
                    agentId: thread.agentId,
                    contextMode: thread.contextMode,
                    status: thread.status,
                    isArchived: this.isArchivedFromRow(thread) ? "true" : "false",
                    archivedAt: thread.archivedAt ?? null,
                    archiveSummaryDocId: thread.archiveSummaryDocId ?? null,
                    isLocked: this.isLockedFromRow(thread) ? "true" : "false",
                    lockedAt: thread.locked_at ?? null,
                    createdAt: thread.createdAt ?? thread.created_at,
                    updatedAt: thread.updatedAt ?? thread.updated_at,
                    messageCount: 0,
                };
            });

            // Include shared threads only in personal context (workspaceId null)
            let sharedSummaries: Array<Omit<ChatThread, 'messages'> & { messageCount: number }> = [];
            if (workspaceId == null || workspaceId === "") {
                const { data: shareAccess } = await supabase
                    .from("thread_share_access")
                    .select("share_id")
                    .eq("user_id", userId)
                    .eq("status", "accepted");
                const shareIds: string[] = shareAccess ? shareAccess.map((a: any) => a.share_id) : [];
                if (shareIds.length > 0) {
                    const { data: shares } = await supabase
                        .from("thread_shares")
                        .select("thread_id")
                        .in("id", shareIds)
                        .eq("is_active", "true");
                    const sharedThreadIds = shares ? [...new Set(shares.map((s: any) => s.thread_id))] : [];
                    if (sharedThreadIds.length > 0) {
                        const { data: sharedRows } = await supabase
                            .from("chat-threads")
                            .select(selectWithArchiveFields)
                            .in("id", sharedThreadIds)
                            .is("workspace_id", null)
                            .is("deleted_at", null)
                            .order("updatedAt", { ascending: false });
                    if (sharedRows && sharedRows.length > 0) {
                        const ownedIds = new Set((data || []).map((t: any) => t.id));
                        sharedSummaries = sharedRows
                            .filter((t: any) => !ownedIds.has(t.id))
                            .map((thread: any) => {
                                // Support both topicIds array (new) and topicId single value (backward compatibility)
                                const topicIds = Array.isArray(thread.topicIds) 
                                    ? thread.topicIds.filter((id: any) => id != null).map((id: any) => String(id))
                                    : [];
                                // Add parentheses around the first two operands
                                const topicId = topicIds.length > 0
                                    ? topicIds[0] // Expose primary topic for single-topic consumers
                                    : ((thread.topicId ?? thread.topic_id) || null);
                                
                                // Support both linkedSpaceIds array (already jsonb) - ensure it's always an array
                                const linkedSpaceIds = Array.isArray(thread.linkedSpaceIds ?? thread.linked_space_ids)
                                    ? (thread.linkedSpaceIds ?? thread.linked_space_ids).filter((id: any) => id != null).map((id: any) => String(id))
                                    : ((thread.linkedSpaceIds ?? thread.linked_space_ids) ? [String(thread.linkedSpaceIds ?? thread.linked_space_ids)] : []);
                                const attachedGitHubRepos = normalizeAttachedGitHubRepos(
                                    thread.attachedGitHubRepos ?? thread.attached_github_repos
                                );
                                
                                return {
                                    id: thread.id,
                                    userId: thread.userId ?? thread.user_id,
                                    title: thread.title,
                                    topic: thread.topic,
                                    topicId: topicId,
                                    topicIds: topicIds.length > 0 ? topicIds : undefined,
                                    tags: thread.tags ?? [],
                                    linkedSpaceIds: linkedSpaceIds,
                                    attachedGitHubRepos,
                                    mergedFromSpaceId: pickMergedFromSpaceId(thread),
                                    agentId: thread.agentId ?? thread.agent_id,
                                    contextMode: thread.contextMode ?? thread.context_mode ?? "MAX",
                                    status: thread.status ?? "active",
                                    isArchived: this.isArchivedFromRow(thread) ? "true" : "false",
                                    archivedAt: thread.archivedAt ?? null,
                                    archiveSummaryDocId: thread.archiveSummaryDocId ?? null,
                                    isLocked: this.isLockedFromRow(thread) ? "true" : "false",
                                    lockedAt: thread.locked_at ?? null,
                                    createdAt: thread.createdAt ?? thread.created_at,
                                    updatedAt: thread.updatedAt ?? thread.updated_at,
                                    messageCount: 0,
                                };
                            });
                    }
                }
                }
            }
            const all = [...ownedSummaries, ...sharedSummaries];
            all.sort((a, b) => {
                const aVal = a.updatedAt ?? a.createdAt ?? 0;
                const bVal = b.updatedAt ?? b.createdAt ?? 0;
                const aTime = new Date(aVal).getTime();
                const bTime = new Date(bVal).getTime();
                return Number.isNaN(bTime) ? 1 : Number.isNaN(aTime) ? -1 : bTime - aTime;
            });
            return all;
        } catch (e: any) {
            if (e?.message?.includes('timeout')) {
                console.error(`getThreadSummaries timed out for user ${userId}`);
                return [];
            }
            throw e;
        }
    }

    /**
     * DS EDIT 6 — keyset-paginated owned-thread summaries (no message bodies).
     * Cursor is ("updatedAt", id) descending — pairs with the
     * idx_chat_threads_user_recency index (migration 072). Supports request
     * abortion via AbortSignal so unmounted components cancel in-flight pages.
     */
    async getThreadSummariesPaginated(
        workspaceId?: string | null,
        cursor?: { updatedAt: string; id: string } | null,
        limit: number = 30,
        signal?: AbortSignal
    ): Promise<{ threads: ThreadSummary[]; nextCursor: { updatedAt: string; id: string } | null; hasMore: boolean }> {
        const userId = await this.getCurrentUserId();
        const select = "id, userId, title, topic, topicId, topicIds, tags, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt";

        let q = supabase
            .from("chat-threads")
            .select(select)
            .eq("userId", userId)
            .is("deleted_at", null)
            .order("updatedAt", { ascending: false })
            .order("id", { ascending: false })
            .limit(limit + 1); // +1 trick: extra row signals hasMore
        if (workspaceId != null && workspaceId !== "") {
            q = q.eq("workspace_id", workspaceId);
        } else {
            q = q.is("workspace_id", null);
        }
        if (cursor?.updatedAt && cursor?.id) {
            q = q.or(
                `updatedAt.lt.${cursor.updatedAt},and(updatedAt.eq.${cursor.updatedAt},id.lt.${cursor.id})`
            );
        }
        if (signal) {
            q = q.abortSignal(signal);
        }

        const { data, error } = await q;
        if (error) {
            throw new Error(`Failed to fetch thread page: ${error.message}`);
        }

        const rows = (data || []) as any[];
        const hasMore = rows.length > limit;
        const pageRows = hasMore ? rows.slice(0, limit) : rows;
        const threads = pageRows.map((thread) => this.mapToThreadSummary(thread, 0));
        const last = pageRows[pageRows.length - 1];
        const nextCursor =
            hasMore && last?.updatedAt && last?.id
                ? { updatedAt: String(last.updatedAt), id: String(last.id) }
                : null;
        return { threads, nextCursor, hasMore };
    }

    /**
     * Get all chat threads for a specific topic (metadata only — messages are empty).
     * @deprecated For list views use {@link getThreadSummariesByTopic}. For full messages use {@link getChatThread}.
     */
    async getChatThreadsByTopic(topicId: string): Promise<ChatThread[]> {
        if (import.meta.env.DEV) {
            console.warn(
                "getChatThreadsByTopic: bulk fetch without messages. Use getThreadSummariesByTopic or getChatThread(id)."
            );
        }
        const summaries = await this.getThreadSummariesByTopic(topicId);
        return summaries.map(({ messageCount: _messageCount, ...rest }) => ({
            ...rest,
            messages: [],
        } as ChatThread));
    }

    /**
     * Lightweight thread summaries for a specific topic (no message bodies).
     */
    async getThreadSummariesByTopic(topicId: string): Promise<ThreadSummary[]> {
        const userId = await this.getCurrentUserId();

        const { data, error } = await supabase
            .from("chat-threads")
            .select(THREAD_SUMMARY_SELECT_WITH_ARCHIVE)
            .eq("userId", userId)
            // Match legacy single topicId column OR the newer topicIds jsonb array
            .or(`topicId.eq.${topicId},topicIds.cs.["${topicId}"]`)
            .order("updatedAt", { ascending: false });

        if (error) {
            console.error("Error fetching thread summaries by topic:", error);
            throw new Error(`Failed to fetch thread summaries by topic: ${error.message}`);
        }

        return (data || []).map((thread: any) => this.mapToThreadSummary(thread, 0));
    }

    /**
     * Touch a thread: update updatedAt so it appears as "most recent" in the sidebar.
     * Only updates owned threads (userId match). Safe to call when opening a thread.
     */
    async touchChatThread(id: string, workspaceId?: string | null): Promise<void> {
        const userId = await this.getCurrentUserId();
        const updateData = { updatedAt: new Date().toISOString() };

        let updateQuery = supabase
            .from("chat-threads")
            .update(updateData)
            .eq("id", id)
            .eq("userId", userId);

        if (workspaceId != null && workspaceId !== "") {
            updateQuery = updateQuery.eq("workspace_id", workspaceId);
        } else {
            updateQuery = updateQuery.is("workspace_id", null);
        }

        const { error } = await updateQuery;

        if (error) {
            console.warn("Failed to touch chat thread (may be shared or missing):", error.message);
        }
    }

    /**
     * Get a single chat thread by ID (owned, invite-shared, or shared-space via RPC).
     * @param workspaceId - When null/undefined, fetch personal thread (workspace_id IS NULL); when set, fetch workspace thread.
     * @param spaceContextId - When opening from a shared space, pass space id for shared_get_chat_thread RPC.
     */
    async getChatThread(
        id: string,
        workspaceId?: string | null,
        spaceContextId?: string | null
    ): Promise<ChatThread | null> {
        const userId = await this.getCurrentUserId();
        const idStr = String(id ?? "").trim();
        const spaceId = spaceContextId ? String(spaceContextId).trim() : null;

        logThreadAccess("start", { id: idStr, workspaceId: workspaceId ?? null, spaceContextId: spaceId, userId });

        let ownedQuery = supabase
            .from("chat-threads")
            .select("id, userId, title, topic, topicId, topicIds, tags, messages, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt")
            .eq("id", idStr)
            .eq("userId", userId);
        if (workspaceId != null && workspaceId !== "") {
            ownedQuery = ownedQuery.eq("workspace_id", workspaceId);
        } else {
            ownedQuery = ownedQuery.is("workspace_id", null);
        }
        const { data: ownedData, error: ownedError } = await ownedQuery.maybeSingle();

        if (!ownedError && ownedData) {
            logThreadAccess("owned", { outcome: "loaded", messageCount: Array.isArray(ownedData.messages) ? ownedData.messages.length : 0 });
            return this.mapToThread(ownedData);
        }

        if (ownedError) {
            console.error("Error fetching chat thread:", ownedError);
            throw new Error(`Failed to fetch chat thread: ${ownedError.message}`);
        }

        logThreadAccess("owned", { outcome: "miss" });

        // Check invite-based thread_shares access
        const { data: shares } = await supabase
            .from("thread_shares")
            .select("id")
            .eq("thread_id", idStr)
            .eq("is_active", "true");

        const shareCount = shares?.length ?? 0;
        logThreadAccess("thread_shares", { shareCount });

        if (shareCount > 0) {
            const shareIds = shares!.map((s: { id: string }) => s.id);
            const { data: access } = await supabase
                .from("thread_share_access")
                .select("id")
                .eq("user_id", userId)
                .eq("status", "accepted")
                .in("share_id", shareIds)
                .maybeSingle();

            if (access) {
                let sharedQuery = supabase
                    .from("chat-threads")
                    .select("id, userId, title, topic, topicId, topicIds, tags, messages, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt")
                    .eq("id", idStr);
                if (workspaceId != null && workspaceId !== "") {
                    sharedQuery = sharedQuery.eq("workspace_id", workspaceId);
                } else {
                    sharedQuery = sharedQuery.is("workspace_id", null);
                }
                const { data: threadData, error: threadError } = await sharedQuery.maybeSingle();

                if (threadError) {
                    throw new Error(`Failed to fetch invite-shared chat thread: ${threadError.message}`);
                }
                if (threadData) {
                    logThreadAccess("thread_shares", { outcome: "loaded" });
                    return this.mapToThread(threadData);
                }
                logThreadAccess("thread_shares", { outcome: "row_missing_after_access" });
            } else {
                logThreadAccess("thread_shares", { outcome: "no_accepted_access" });
            }
        }

        // Shared-space access (editor/viewer on threads linked to a space)
        const rpcPayload: { p_id: string; p_space_id?: string } = { p_id: idStr };
        if (spaceId) rpcPayload.p_space_id = spaceId;

        if (spaceId && DEBUG_THREAD_ACCESS) {
            const { data: acc, error: accErr } = await supabase.rpc("effective_thread_access_in_space", {
                p_thread_id: idStr,
                p_space_id: spaceId,
                p_actor_id: userId,
            });
            logThreadAccess("shared_space_precheck", {
                spaceId,
                effective_thread_access_in_space: acc,
                error: accErr?.message ?? null,
            });
        }

        logThreadAccess("shared_space_rpc", { payload: rpcPayload });
        const { data: sharedRow, error: rpcError } = await supabase.rpc("shared_get_chat_thread", {
            payload: rpcPayload,
        });

        if (rpcError) {
            console.error("shared_get_chat_thread:", rpcError);
            throw new Error(rpcError.message || "Failed to load thread (shared space)");
        }

        if (!sharedRow) {
            logThreadAccess("shared_space_rpc", { outcome: "not_found_or_denied" });
            return null;
        }

        const raw =
            typeof sharedRow === "object" && sharedRow !== null && !Array.isArray(sharedRow)
                ? sharedRow
                : JSON.parse(String(sharedRow));
        logThreadAccess("shared_space_rpc", {
            outcome: "loaded",
            ownerUserId: raw.userId,
            messageCount: Array.isArray(raw.messages) ? raw.messages.length : 0,
        });
        return this.mapToThread(raw);
    }

    private buildSharedThreadRpcUpdates(updates: Partial<InsertChatThread>): Record<string, unknown> {
        const pUpdates: Record<string, unknown> = {};
        if (updates.title !== undefined) pUpdates.title = updates.title;
        if (updates.topic !== undefined) pUpdates.topic = updates.topic;
        if (updates.tags !== undefined) pUpdates.tags = updates.tags;
        if (updates.messages !== undefined) pUpdates.messages = updates.messages;
        if (updates.linkedSpaceIds !== undefined) {
            pUpdates.linkedSpaceIds = Array.isArray(updates.linkedSpaceIds)
                ? updates.linkedSpaceIds
                : updates.linkedSpaceIds
                  ? [updates.linkedSpaceIds]
                  : [];
        }
        if ((updates as { attachedGitHubRepos?: unknown }).attachedGitHubRepos !== undefined) {
            pUpdates.attachedGitHubRepos = normalizeAttachedGitHubRepos(
                (updates as { attachedGitHubRepos?: unknown }).attachedGitHubRepos
            );
        }
        // Keep topicId and topicIds in sync (reads prefer topicIds; a stale value in either
        // field would mask the update). Mirrors the direct-update path in updateChatThread.
        if ((updates as any).topicIds !== undefined) {
            const topicIds = Array.isArray((updates as any).topicIds)
                ? (updates as any).topicIds.filter((id: any) => id != null)
                : [];
            pUpdates.topicIds = topicIds.length > 0 ? topicIds : null;
            pUpdates.topicId = null;
        } else if (updates.topicId !== undefined) {
            pUpdates.topicId = updates.topicId;
            pUpdates.topicIds = updates.topicId ? [updates.topicId] : null;
        }
        if (updates.contextMode !== undefined) pUpdates.contextMode = updates.contextMode;
        if ((updates as any).chatMode !== undefined) pUpdates.chatMode = (updates as any).chatMode;
        if (updates.status !== undefined) pUpdates.status = updates.status;
        if ((updates as any).isLocked !== undefined) pUpdates.isLocked = (updates as any).isLocked;
        if ((updates as any).lockedAt !== undefined) pUpdates.lockedAt = (updates as any).lockedAt;
        if ((updates as any).isArchived !== undefined) pUpdates.isArchived = (updates as any).isArchived;
        if ((updates as any).archivedAt !== undefined) pUpdates.archivedAt = (updates as any).archivedAt;
        if ((updates as any).archiveSummaryDocId !== undefined) {
            pUpdates.archiveSummaryDocId = (updates as any).archiveSummaryDocId;
        }
        if ((updates as any).includeInRag !== undefined) pUpdates.includeInRag = (updates as any).includeInRag;
        if (updates.agentId !== undefined) pUpdates.agentId = updates.agentId;
        return pUpdates;
    }

    private async invokeSharedUpdateChatThread(
        id: string,
        spaceContextId: string,
        pUpdates: Record<string, unknown>
    ): Promise<ChatThread | null> {
        const { data: rpcData, error: rpcErr } = await supabase.rpc("shared_update_chat_thread", {
            p_id: id,
            p_space_id: spaceContextId,
            p_updates: pUpdates as any,
        });
        if (rpcErr) {
            throw new Error(rpcErr.message || "Failed to update thread (shared)");
        }
        if (!rpcData) return null;
        const raw = typeof rpcData === "object" && rpcData !== null ? rpcData : JSON.parse(String(rpcData));
        const thread = this.mapToThread(raw);
        if (pUpdates.messages !== undefined) {
            this.maybeScheduleThreadEmbed(id, { messages: pUpdates.messages as unknown[] }, thread);
        }
        return thread;
    }

    private async getThreadOwnerUserId(threadId: string): Promise<string | null> {
        const { data } = await supabase.from("chat-threads").select("userId").eq("id", threadId).maybeSingle();
        return data?.userId != null ? String(data.userId) : null;
    }

    /**
     * Create a new chat thread.
     * @param threadData - Thread fields
     * @param workspaceId - When null/undefined: personal thread (workspace_id IS NULL). When set: thread belongs to that workspace.
     */
    async createChatThread(threadData: InsertChatThread, workspaceId?: string | null): Promise<ChatThread> {
        const userId = await this.getCurrentUserId();
        
        // Handle topicIds array and topicId (backward compatibility)
        // DS EDIT 7: deduplicate on write — arrays are canonical, never ["a","a"]
        const topicIds = dedupeIds(
            (threadData as any).topicIds !== undefined
                ? ((threadData as any).topicIds || [])
                : (threadData.topicId ? [threadData.topicId] : [])
        );
        const topicId = (threadData as any).topicIds !== undefined 
            ? null // Clear old topicId when topicIds array is used
            : (threadData.topicId || null);
        
        // Handle linkedSpaceIds - ensure it's always an array (deduped, DS EDIT 7)
        const linkedSpaceIds = dedupeIds(
            Array.isArray(threadData.linkedSpaceIds)
                ? threadData.linkedSpaceIds
                : (threadData.linkedSpaceIds ? [threadData.linkedSpaceIds] : [])
        );
        const attachedGitHubRepos = normalizeAttachedGitHubRepos(
            (threadData as { attachedGitHubRepos?: unknown }).attachedGitHubRepos
        );
        
        const now = new Date().toISOString();
        const threadToInsert: any = {
            userId: userId,
            title: threadData.title || null,
            topic: threadData.topic || null,
            tags: threadData.tags || [],
            messages: threadData.messages || [],
            linkedSpaceIds: linkedSpaceIds,
            attachedGitHubRepos,
            agentId: threadData.agentId || null,
            topicId: topicId,
            topicIds: topicIds.length > 0 ? topicIds : null, // Store as jsonb array, null if empty
            contextMode: threadData.contextMode || "MAX",
            chatMode: (threadData as any).chatMode || "normal",
            status: threadData.status || "active",
            createdAt: now,
            updatedAt: now,
        };
        if (workspaceId != null && workspaceId !== "") {
            threadToInsert.workspace_id = workspaceId;
        } else {
            threadToInsert.workspace_id = null;
        }

        console.log("Creating chat thread with data:", { ...threadToInsert, messages: threadToInsert.messages?.length || 0 });
        
        const { data, error } = await supabase
            .from("chat-threads")
            .insert(threadToInsert)
            .select()
            .single();

        if (data) {
            console.log("Chat thread created successfully:", data.id);
        }

        if (error) {
            console.error("Error creating chat thread:", error);
            throw new Error(`Failed to create chat thread: ${error.message}`);
        }

        if (!data) {
            throw new Error("Failed to create chat thread: No data returned");
        }

        const created = this.mapToThread(data);
        scheduleThreadBrief(String(created.userId ?? userId), String(created.id), created.title, created.messages);
        void import("@/services/threadAuditService").then(({ threadAuditService }) =>
            threadAuditService.logThreadAction("create", String(created.id), {
                title: created.title,
                workspace_id: workspaceId ?? null,
            })
        );
        return created;
    }

    /**
     * Update an existing chat thread (for current user only).
     * @param workspaceId - When undefined, no workspace scoping (updates the owned thread wherever it lives).
     *                      When null, personal thread only; when set, that workspace's thread only.
     */
    async updateChatThread(
        id: string,
        updates: Partial<InsertChatThread>,
        workspaceId?: string | null,
        /** When editing another member's thread in a shared space, pass the space id (uses RPC). */
        spaceContextId?: string | null
    ): Promise<ChatThread | null> {
        try {
            const userId = await this.getCurrentUserId();

            if (spaceContextId) {
                const ownerId = await this.getThreadOwnerUserId(id);
                const isOtherUsersThread = ownerId != null && ownerId !== userId;
                const pUpdates = this.buildSharedThreadRpcUpdates(updates);
                if (
                    Object.keys(pUpdates).length > 0 &&
                    (isOtherUsersThread || updates.linkedSpaceIds !== undefined)
                ) {
                    if (DEBUG_THREAD_ACCESS) {
                        console.log("[chatThreadsService.updateChatThread] shared RPC", {
                            id,
                            spaceContextId,
                            isOtherUsersThread,
                            keys: Object.keys(pUpdates),
                        });
                    }
                    return await this.invokeSharedUpdateChatThread(id, spaceContextId, pUpdates);
                }
            }

            const updateData: any = {
                updatedAt: new Date().toISOString(),
            };

            if (updates.title !== undefined) updateData.title = updates.title;
            if (updates.topic !== undefined) updateData.topic = updates.topic;
            if (updates.tags !== undefined) updateData.tags = updates.tags;
            if (updates.messages !== undefined) updateData.messages = updates.messages;

            // Handle linkedSpaceIds - ensure it's always an array (deduped, DS EDIT 7)
            if (updates.linkedSpaceIds !== undefined) {
                updateData.linkedSpaceIds = dedupeIds(
                    Array.isArray(updates.linkedSpaceIds)
                        ? updates.linkedSpaceIds
                        : (updates.linkedSpaceIds ? [updates.linkedSpaceIds] : [])
                );
            }

            if ((updates as { attachedGitHubRepos?: unknown }).attachedGitHubRepos !== undefined) {
                updateData.attachedGitHubRepos = normalizeAttachedGitHubRepos(
                    (updates as { attachedGitHubRepos?: unknown }).attachedGitHubRepos
                );
            }

            if (updates.agentId !== undefined) updateData.agentId = updates.agentId;

            // Handle topicIds array and topicId (backward compatibility)
            if ((updates as any).topicIds !== undefined) {
                // New format: topicIds array (deduped on write, DS EDIT 7)
                const topicIds = dedupeIds(
                    Array.isArray((updates as any).topicIds) ? (updates as any).topicIds : []
                );
                updateData.topicIds = topicIds.length > 0 ? topicIds : null;
                updateData.topicId = null; // Clear old topicId when topicIds array is used
            } else if (updates.topicId !== undefined) {
                // Old format: single topicId. Keep topicIds in sync — reads prefer topicIds,
                // so a stale array would otherwise mask this update ("topic forgotten" bug).
                updateData.topicId = updates.topicId;
                updateData.topicIds = updates.topicId ? [updates.topicId] : null;
            }

            if (updates.contextMode !== undefined) updateData.contextMode = updates.contextMode;
            if ((updates as any).chatMode !== undefined) updateData.chatMode = (updates as any).chatMode;
            if (updates.status !== undefined) updateData.status = updates.status;
            if ((updates as any).isLocked !== undefined) updateData.is_locked = (updates as any).isLocked;
            if ((updates as any).lockedAt !== undefined) updateData.locked_at = (updates as any).lockedAt;
            if ((updates as any).isArchived !== undefined) {
                updateData.status = (updates as any).isArchived ? "archived" : "active";
                updateData.isArchived = (updates as any).isArchived ? "true" : "false"; // Schema: isArchived (camelCase)
            }
            if ((updates as any).archivedAt !== undefined) updateData.archivedAt = (updates as any).archivedAt; // Schema: archivedAt (camelCase)
            if ((updates as any).archiveSummaryDocId !== undefined) {
                updateData.archiveSummaryDocId = (updates as any).archiveSummaryDocId; // Schema: archiveSummaryDocId (camelCase)
            }
            if ((updates as any).includeInRag !== undefined) {
                updateData.includeInRag = (updates as any).includeInRag;
            }

            let updateQuery = supabase
                .from("chat-threads")
                .update(updateData)
                .eq("id", id)
                .eq("userId", userId);
            // Only scope by workspace when the caller explicitly provided one.
            // id + userId already uniquely identify the row; filtering workspace_id IS NULL for
            // callers that omit it (e.g. cross-workspace list views) silently updated 0 rows.
            if (workspaceId !== undefined) {
                if (workspaceId != null && workspaceId !== "") {
                    updateQuery = updateQuery.eq("workspace_id", workspaceId);
                } else {
                    updateQuery = updateQuery.is("workspace_id", null);
                }
            }
            const { data, error } = await updateQuery.select().maybeSingle();
            console.log("Update chat thread result:", { data, error });

            if (error) {
                console.error("Error updating chat thread:", error);
                throw new Error(`Failed to update chat thread: ${error.message}`);
            }

            // Fallback for shared-space edits where direct owner-scoped update returns no row.
            if (!data && spaceContextId) {
                const pUpdates = this.buildSharedThreadRpcUpdates(updates);
                if (Object.keys(pUpdates).length > 0) {
                    return await this.invokeSharedUpdateChatThread(id, spaceContextId, pUpdates);
                }
            }

            if (!data) return null;

            const thread = this.mapToThread(data);
            this.maybeScheduleThreadEmbed(id, updates, thread);
            if (updates.messages !== undefined) {
                scheduleThreadBrief(String(thread.userId ?? userId), String(thread.id), thread.title, thread.messages);
            }
            void import("@/services/threadAuditService").then(({ threadAuditService }) =>
                threadAuditService.logThreadAction("update", String(id), {
                    workspace_id: workspaceId ?? null,
                    space_context_id: spaceContextId ?? null,
                    fields: Object.keys(updates),
                })
            );
            return thread;
        } catch (e: any) {
            console.log(`-------------------e--`)
            console.log(
                e
            )
            throw e;
        }
    }

    /**
     * Set includeInRag for all threads linked to a topic (bulk update)
     */
    async bulkUpdateThreadsIncludeInRagByTopic(topicId: string, includeInRag: boolean): Promise<number> {
        const userId = await this.getCurrentUserId();
        const { data, error } = await supabase
            .from("chat-threads")
            .update({ includeInRag })
            .eq("userId", userId)
            // Match legacy single topicId column OR the newer topicIds jsonb array
            .or(`topicId.eq.${topicId},topicIds.cs.["${topicId}"]`)
            .select("id");

        if (error) {
            console.error("Error bulk updating thread includeInRag:", error);
            throw new Error(`Failed to update threads: ${error.message}`);
        }
        return data?.length ?? 0;
    }

    /**
     * Soft-delete a chat thread (30-day trash retention before purge).
     */
    async deleteChatThread(id: string, spaceContextId?: string | null): Promise<boolean> {
        const userId = await this.getCurrentUserId();
        const now = new Date().toISOString();

        const { data: row } = await supabase
            .from("chat-threads")
            .select("userId, title, workspace_id")
            .eq("id", id)
            .maybeSingle();
        const ownerId = (row as any)?.userId;
        const isOwn = ownerId === userId;

        if (!isOwn) {
            if (!spaceContextId) {
                throw new Error("Cannot delete this thread");
            }
            const { error: rpcErr } = await supabase.rpc("shared_delete_chat_thread", {
                p_id: id,
                p_space_id: spaceContextId,
            });
            if (rpcErr) {
                throw new Error(rpcErr.message || "Failed to delete thread");
            }
            return true;
        }

        const { error } = await supabase
            .from("chat-threads")
            .update({
                deleted_at: now,
                status: "disabled",
                updatedAt: now,
            })
            .eq("id", id)
            .eq("userId", userId)
            .is("deleted_at", null);

        if (error) {
            console.error("Error deleting chat thread:", error);
            throw new Error(`Failed to delete chat thread: ${error.message}`);
        }

        void import("@/services/threadAuditService").then(({ threadAuditService }) =>
            threadAuditService.logThreadAction("delete", String(id), {
                title: (row as any)?.title,
                workspace_id: (row as any)?.workspace_id ?? null,
                soft_delete: true,
            })
        );
        return true;
    }

    /**
     * Restore a soft-deleted thread from trash.
     */
    async restoreChatThread(id: string): Promise<boolean> {
        const userId = await this.getCurrentUserId();
        const now = new Date().toISOString();

        const { data, error } = await supabase
            .from("chat-threads")
            .update({
                deleted_at: null,
                status: "active",
                updatedAt: now,
            })
            .eq("id", id)
            .eq("userId", userId)
            .not("deleted_at", "is", null)
            .select("id, title, workspace_id")
            .maybeSingle();

        if (error) {
            throw new Error(`Failed to restore thread: ${error.message}`);
        }
        if (!data) {
            throw new Error("Thread not found in trash");
        }

        void import("@/services/threadAuditService").then(({ threadAuditService }) =>
            threadAuditService.logThreadAction("restore", String(id), {
                title: (data as any).title,
                workspace_id: (data as any).workspace_id ?? null,
            })
        );
        return true;
    }

    /**
     * Lock or unlock a chat thread
     * Matches server implementation: PATCH /api/chat-threads/:id/lock
     */
    async lockChatThread(
        id: string,
        lock: boolean,
        workspaceId?: string | null,
        spaceContextId?: string | null
    ): Promise<ChatThread | null> {
        const lockedAt = lock ? new Date().toISOString() : null;
        return await this.updateChatThread(id, { isLocked: lock, lockedAt } as any, workspaceId, spaceContextId);
    }

    /**
     * Generate metadata for a chat thread using AI
     * Analyzes the entire conversation to create a pithy, accurate title
     * @param workspaceId - When set, fetch/update workspace thread; when null, personal.
     * @param spaceContextId - When editing in a shared space, pass space id for RPC access.
     */
    async generateMetadata(
        id: string,
        options?: {
            workspaceId?: string | null;
            spaceContextId?: string | null;
        }
    ): Promise<ChatThread | null> {
        const workspaceId = options?.workspaceId ?? null;
        const spaceContextId = options?.spaceContextId ?? null;
        const thread = await this.getChatThread(id, workspaceId, spaceContextId);
        if (!thread) return null;

        const existingTitle = thread.title?.trim();
        if (existingTitle && existingTitle !== "Untitled") {
            return thread;
        }
        
        const messages = thread.messages as any[];
        if (!messages || messages.length === 0) return thread;
        
        // Build conversation summary for AI to analyze
        const conversationText = messages.map((msg: any) => {
            const role = msg.role === "user" ? "User" : "Assistant";
            const content = typeof msg.content === 'string' 
                ? msg.content 
                : Array.isArray(msg.content) 
                    ? (msg.content.find((c: any) => c.type === 'text')?.text || '')
                    : '';
            return `${role}: ${content}`;
        }).join("\n");
        
        if (!conversationText.trim()) return thread;

        const firstUserMsg = messages.find((m: any) => m.role === "user");
        let fallbackText = "";
        if (firstUserMsg) {
            const content =
                typeof firstUserMsg.content === "string"
                    ? firstUserMsg.content
                    : Array.isArray(firstUserMsg.content)
                      ? firstUserMsg.content.find((c: any) => c.type === "text")?.text || ""
                      : "";
            fallbackText = content.replace(/^\[Deep Research\]\s*/i, "").trim();
        }

        const title = await autoTitleFromPlainText(conversationText, "conversation", { fallbackText });
        if (!title) return thread;

        return await this.updateChatThread(id, { title }, workspaceId, spaceContextId);
    }

    /**
     * Archive a chat thread with AI-generated summary
     * Creates a knowledge base document with the summary and marks thread as archived.
     * @param workspaceId - When set, thread is fetched/updated in that workspace; when null, personal.
     */
    async archiveThread(
        id: string,
        summaryContent: string,
        summaryTitle: string,
        spaceId?: string | null,
        topicId?: string | null,
        workspaceId?: string | null,
        summaryMetadata?: Record<string, unknown> | null
    ): Promise<{ thread: ChatThread | null; documentId: string | null }> {
        const thread = await this.getChatThread(id, workspaceId ?? null);
        if (!thread) {
            return { thread: null, documentId: null };
        }

        // Import knowledgeBaseService dynamically to avoid circular imports
        const { knowledgeBaseService } = await import("./knowledgeBaseService");

        // Create knowledge base document with the summary (same workspace as thread)
        const document = await knowledgeBaseService.createKnowledgeBaseItem({
            title: summaryTitle,
            content: summaryContent,
            type: "archive_summary",
            spaceId: spaceId || (thread.linkedSpaceIds && thread.linkedSpaceIds.length > 0 ? thread.linkedSpaceIds[0] : null),
            topicId: topicId || (thread as any).topicId || null,
            metadata: {
                sourceThreadId: id,
                sourceThreadTitle: thread.title,
                archivedAt: new Date().toISOString(),
                ...(summaryMetadata && typeof summaryMetadata === "object" ? summaryMetadata : {}),
            }
        }, workspaceId ?? null);

        // Mark thread as archived
        const now = new Date().toISOString();
        const updatePayload = {
            isArchived: "true", // Schema: isArchived (camelCase)
            archivedAt: now, // Schema: archivedAt (camelCase)
            archiveSummaryDocId: document.id, // Schema: archiveSummaryDocId (camelCase)
            status: "archived",
            updatedAt: now
        };
        let { data, error } = await supabase
            .from("chat-threads")
            .update(updatePayload)
            .eq("id", id)
            .select()
            .single();

        if (error) {
            console.error("Error archiving thread:", error);
            throw new Error(`Failed to archive thread: ${error.message}`);
        }

        return { thread: data as ChatThread, documentId: document.id };
    }

    /**
     * Get archived chat threads for the current user
     */
    async getArchivedThreads(): Promise<ChatThread[]> {
        const userId = await this.getCurrentUserId();
        
        const { data, error } = await supabase
            .from("chat-threads")
            .select("id, userId, title, topic, topicId, topicIds, tags, messages, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt")
            .eq("userId", userId)
            .order("updatedAt", { ascending: false });

        if (error) {
            console.error("Error fetching archived threads:", error);
            throw new Error(`Failed to fetch archived threads: ${error.message}`);
        }

        return (data || [])
            .map((thread: any) => this.mapToThread(thread))
            .filter((thread: any) => this.isArchivedFromRow(thread));
    }

    /**
     * Get active (non-archived) chat threads for the current user
     */
    async getActiveThreads(): Promise<ChatThread[]> {
        const userId = await this.getCurrentUserId();
        
        const { data, error } = await supabase
            .from("chat-threads")
            .select("id, userId, title, topic, topicId, topicIds, tags, messages, linkedSpaceIds, attachedGitHubRepos, mergedFromSpaceId, agentId, contextMode, status, isArchived, archivedAt, archiveSummaryDocId, is_locked, locked_at, includeInRag, createdAt, updatedAt")
            .eq("userId", userId)
            .order("updatedAt", { ascending: false });

        if (error) {
            console.error("Error fetching active threads:", error);
            throw new Error(`Failed to fetch active threads: ${error.message}`);
        }

        return (data || [])
            .map((thread: any) => this.mapToThread(thread))
            .filter((thread: any) => !this.isArchivedFromRow(thread));
    }

    /**
     * Unarchive a thread
     */
    async unarchiveThread(id: string): Promise<ChatThread | null> {
        const now = new Date().toISOString();
        const updatePayload = {
            isArchived: "false", // Schema: isArchived (camelCase)
            archivedAt: null, // Schema: archivedAt (camelCase)
            status: "active",
            updatedAt: now
        };
        let { data, error } = await supabase
            .from("chat-threads")
            .update(updatePayload)
            .eq("id", id)
            .select()
            .single();

        if (error) {
            if (error.message?.includes("column") && error.message?.includes("does not exist")) {
                const fallback = await supabase
                    .from("chat-threads")
                    .update({ status: "active", updatedAt: now })
                    .eq("id", id)
                    .select()
                    .single();
                if (fallback.error) {
                    console.error("Error unarchiving thread:", fallback.error);
                    throw new Error(`Failed to unarchive thread: ${fallback.error.message}`);
                }
                data = fallback.data;
            } else {
                console.error("Error unarchiving thread:", error);
                throw new Error(`Failed to unarchive thread: ${error.message}`);
            }
        }

        return this.mapToThread(data);
    }

    /**
     * Move a thread to a different topic
     */
    async moveThreadToTopic(threadId: string, targetTopicId: string | null): Promise<ChatThread | null> {
        const userId = await this.getCurrentUserId();
        
        // Verify thread exists and belongs to user
        const thread = await this.getChatThread(threadId);
        if (!thread) {
            throw new Error("Thread not found");
        }

        // If target topic is provided, verify it exists
        if (targetTopicId) {
            const { data: topic, error: topicError } = await supabase
                .from("topics")
                .select("id, space_id")
                .eq("id", targetTopicId)
                .single();

            if (topicError || !topic) {
                throw new Error("Target topic not found");
            }
        }

        const { data, error } = await supabase
            .from("chat-threads")
            .update({
                topicId: targetTopicId,
                updatedAt: new Date().toISOString()
            })
            .eq("id", threadId)
            .eq("userId", userId)
            .select()
            .single();

        if (error) {
            console.error("Error moving thread to topic:", error);
            throw new Error(`Failed to move thread: ${error.message}`);
        }

        return this.mapToThread(data);
    }

    /**
     * Move a thread to a different space (update linked spaces)
     */
    async moveThreadToSpace(threadId: string, targetSpaceId: string): Promise<ChatThread | null> {
        const userId = await this.getCurrentUserId();
        
        // Verify thread exists and belongs to user
        const thread = await this.getChatThread(threadId);
        if (!thread) {
            throw new Error("Thread not found");
        }

        // Verify user owns the target space
        const { data: targetSpace, error: targetSpaceError } = await supabase
            .from("mastermind")
            .select("id, user_id")
            .eq("id", targetSpaceId)
            .eq("user_id", userId)
            .single();

        if (targetSpaceError || !targetSpace) {
            throw new Error("You don't have permission to move to the target space");
        }

        // Replace all linked space IDs with the target space
        const { data, error } = await supabase
            .from("chat-threads")
            .update({
                linkedSpaceIds: [targetSpaceId],
                topicId: null, // Clear topic since we're moving to a different space
                updatedAt: new Date().toISOString()
            })
            .eq("id", threadId)
            .eq("userId", userId)
            .select()
            .single();

        if (error) {
            console.error("Error moving thread to space:", error);
            throw new Error(`Failed to move thread: ${error.message}`);
        }

        return this.mapToThread(data);
    }
}

/**
 * Create and export a singleton instance
 */
export const chatThreadsService = new ChatThreadsService();
